import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import ProductColorField from './ProductColorField';
import { Product, Recipe, PackageType, Ingredient } from '../types/interfaces';
import { ProductClass, ProductType, Style } from '../types/enums';
import RecipeModal from './RecipeModal';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../App.css';
import { apiFetch } from '../utils/api';
import { API_BASE_URL } from '../config';

const ProductDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [items, setItems] = useState<{ name: string; type: string; enabled: number }[]>([]);
  const [packageTypes, setPackageTypes] = useState<PackageType[]>([]);
  const [availablePackageTypes, setAvailablePackageTypes] = useState<{ name: string; volume: number; enabled: number }[]>([]);
  const [showRecipeModal, setShowRecipeModal] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState<Recipe | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const styleOptions: { [key in ProductType]?: Style[] } = {
    [ProductType.MaltBeverage]: [
      Style.Ale,
      Style.Lager,
      Style.IPA,
      Style.Stout,
      Style.Porter,
      Style.Pilsner,
      Style.Wheat,
      Style.Other,
    ],
    [ProductType.Seltzer]: [Style.Other],
    [ProductType.GrapeWine]: [Style.Red, Style.White, Style.Rosé, Style.Champagne, Style.Sherry, Style.Port, Style.Madeira],
    [ProductType.SparklingWine]: [Style.Champagne, Style.Other],
    [ProductType.CarbonatedWine]: [Style.Other],
    [ProductType.FruitWine]: [Style.Other],
    [ProductType.Cider]: [Style.Other],
    [ProductType.OtherAgriculturalWine]: [Style.Other],
    [ProductType.Whisky]: [Style.Bourbon, Style.Scotch, Style.Rye, Style.Other],
    [ProductType.Gin]: [Style.LondonDry, Style.Genever, Style.OldTom, Style.Other],
    [ProductType.Vodka]: [Style.Other],
    [ProductType.NeutralSpirits]: [Style.Other],
    [ProductType.Rum]: [Style.SpicedRum, Style.WhiteRum, Style.Other],
    [ProductType.Tequila]: [Style.Blanco, Style.Reposado, Style.Añejo, Style.Other],
    [ProductType.CordialsLiqueurs]: [Style.Other],
    [ProductType.FlavoredSpirits]: [Style.Cocktail, Style.Other],
    [ProductType.DistilledSpiritsSpecialty]: [Style.Cocktail, Style.Other],
  };

  const spiritsTypes = [
    ProductType.Whisky,
    ProductType.Gin,
    ProductType.Vodka,
    ProductType.NeutralSpirits,
    ProductType.Rum,
    ProductType.Tequila,
    ProductType.CordialsLiqueurs,
    ProductType.FlavoredSpirits,
    ProductType.DistilledSpiritsSpecialty,
  ];

  const wineTypes = [
    ProductType.GrapeWine,
    ProductType.SparklingWine,
    ProductType.CarbonatedWine,
    ProductType.FruitWine,
    ProductType.Cider,
    ProductType.OtherAgriculturalWine,
  ];

  const beerTypes = [ProductType.MaltBeverage, ProductType.Seltzer];

  const getAuthHeaders = useCallback(async () => {
    let token = localStorage.getItem('token');
    if (!token) {
      console.error('[ProductDetails] No token found, redirecting to login');
      navigate('/login');
      return null;
    }
    if (!token.startsWith('eyJ')) {
      console.error('[ProductDetails] Invalid token format, redirecting to login');
      navigate('/login');
      return null;
    }
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const now = Math.floor(Date.now() / 1000);
      if (payload.exp < now) {
        console.error('[ProductDetails] Token expired, redirecting to login', { exp: payload.exp, now });
        navigate('/login');
        return null;
      }
    } catch (err) {
      console.error('[ProductDetails] Token parsing error:', err);
      navigate('/login');
      return null;
    }
    return {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    };
  }, [navigate]);

  const mapServerToFrontend = (serverProduct: any): Product => {
    let mapped: Product = { ...serverProduct };
    let serverClass = serverProduct.class;
    let serverType = serverProduct.type;
    let serverStyle = serverProduct.style as Style | undefined;

    if (serverClass === 'Distilled') {
      mapped.class = ProductClass.Spirits;
    } else if (serverClass === 'Soda') {
      mapped.class = ProductClass.Soda;
    }

    let frontendType: ProductType | undefined;
    let frontendStyle: Style | undefined = serverStyle;

    if (mapped.class === ProductClass.Beer) {
      if (serverType === 'Malt') {
        frontendType = ProductType.MaltBeverage;
      } else if (serverType === 'Seltzer') {
        frontendType = ProductType.Seltzer;
      } else {
        frontendType = ProductType.MaltBeverage; // default
      }
    } else if (mapped.class === ProductClass.Soda) {
      frontendType = ProductType.Seltzer;
      frontendStyle = Style.Other;
    } else if (mapped.class === ProductClass.Wine) {
      if (serverType === 'Cider') {
        frontendType = ProductType.Cider;
      } else {
        frontendType = ProductType.GrapeWine; // default
        if (serverStyle) {
          if (serverStyle === 'Other') {
            frontendType = ProductType.GrapeWine;
          } else {
            for (const t of wineTypes) {
              if (styleOptions[t]?.includes(serverStyle)) {
                frontendType = t;
                break;
              }
            }
          }
        }
      }
    } else if (mapped.class === ProductClass.Spirits) {
      frontendType = ProductType.NeutralSpirits; // default
      if (serverStyle) {
        if (serverStyle === 'Other') {
          frontendType = ProductType.NeutralSpirits;
        } else {
          for (const t of spiritsTypes) {
            if (styleOptions[t]?.includes(serverStyle)) {
              frontendType = t;
              break;
            }
          }
        }
      }
    } else {
      frontendType = serverType as ProductType;
    }

    mapped.type = frontendType;
    mapped.style = frontendStyle;

    return mapped;
  };

  const mapToServerClass = (classValue: string | undefined): string => {
    if (classValue === ProductClass.Spirits) {
      return 'Distilled';
    }
    return classValue || '';
  };

  const mapToServerType = (classValue: string | undefined, typeValue: string | undefined): string => {
    if (classValue === ProductClass.Beer) {
      if (typeValue === ProductType.MaltBeverage) return 'Malt';
      if (typeValue === ProductType.Seltzer) return 'Seltzer';
    } else if (classValue === ProductClass.Soda) {
      return 'Seltzer';
    } else if (classValue === ProductClass.Wine) {
      if (typeValue === ProductType.Cider) return 'Cider';
      return 'Wine';
    } else if (classValue === ProductClass.Spirits) {
      return 'Spirits';
    }
    return typeValue || '';
  };

  const fetchProduct = useCallback(async () => {
    const headers = await getAuthHeaders();
    if (!headers) return;
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/products/${id}`, { headers });
      if (!res.ok) {
        const text = await res.text();
        console.error('[ProductDetails] Fetch product response:', { status: res.status, text });
        if (res.status === 401) {
          console.error('[ProductDetails] Unauthorized, redirecting to login');
          navigate('/login');
          throw new Error('Unauthorized: Please log in again');
        }
        throw new Error(`HTTP error! status: ${res.status}, Response: ${text.slice(0, 50)}`);
      }
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await res.text();
        console.error('[ProductDetails] Fetch product non-JSON response:', { status: res.status, text });
        throw new Error(`Invalid response: Expected JSON, got ${contentType || 'unknown content-type'}`);
      }
      const data = await res.json();
      console.log('[ProductDetails] Fetched product:', data);
      const mappedProduct = mapServerToFrontend(data);
      setProduct(mappedProduct);
      setPackageTypes(data.packageTypes || []);
    } catch (err: any) {
      console.error('[ProductDetails] Fetch product error:', err);
      setError(`Failed to fetch product: ${err.message}`);
    }
  }, [id, getAuthHeaders, navigate]);

  const fetchRecipes = useCallback(async () => {
    const headers = await getAuthHeaders();
    if (!headers) return;
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/recipes?productId=${id}`, { headers });
      if (!res.ok) {
        const text = await res.text();
        console.error('[ProductDetails] Fetch recipes response:', { status: res.status, text });
        if (res.status === 401) {
          console.error('[ProductDetails] Unauthorized, redirecting to login');
          navigate('/login');
          throw new Error('Unauthorized: Please log in again');
        }
        throw new Error(`HTTP error! status: ${res.status}, Response: ${text.slice(0, 50)}`);
      }
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await res.text();
        console.error('[ProductDetails] Fetch recipes non-JSON response:', { status: res.status, text });
        throw new Error(`Invalid response: Expected JSON, got ${contentType || 'unknown content-type'}`);
      }
      const data = await res.json();
      console.log('[ProductDetails] Fetched recipes:', data);
      const list = Array.isArray(data) ? data : [];
      // Defensive filter — server filters by productId; keep only matching rows.
      setRecipes(list.filter((r: Recipe) => String(r.productId) === String(id)));
    } catch (err: any) {
      console.error('[ProductDetails] Fetch recipes error:', err);
      setError(`Failed to fetch recipes: ${err.message}`);
    }
  }, [id, getAuthHeaders, navigate]);

  const fetchItems = useCallback(async () => {
    const headers = await getAuthHeaders();
    if (!headers) return;
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/items`, { headers });
      if (!res.ok) {
        const text = await res.text();
        console.error('[ProductDetails] Fetch items response:', { status: res.status, text });
        if (res.status === 401) {
          console.error('[ProductDetails] Unauthorized, redirecting to login');
          navigate('/login');
          throw new Error('Unauthorized: Please log in again');
        }
        throw new Error(`HTTP error! status: ${res.status}, Response: ${text.slice(0, 50)}`);
      }
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await res.text();
        console.error('[ProductDetails] Fetch items non-JSON response:', { status: res.status, text });
        throw new Error(`Invalid response: Expected JSON, got ${contentType || 'unknown content-type'}`);
      }
      const data = await res.json();
      console.log('[ProductDetails] Fetched items:', data);
      setItems(data || []);
    } catch (err: any) {
      console.error('[ProductDetails] Fetch items error:', err);
      setError(`Failed to fetch items: ${err.message}`);
    }
  }, [getAuthHeaders, navigate]);

  const fetchPackageTypes = useCallback(async () => {
    const headers = await getAuthHeaders();
    if (!headers) return;
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/package-types`, { headers });
      if (!res.ok) {
        const text = await res.text();
        console.error('[ProductDetails] Fetch package types response:', { status: res.status, text });
        if (res.status === 401) {
          console.error('[ProductDetails] Unauthorized, redirecting to login');
          navigate('/login');
          throw new Error('Unauthorized: Please log in again');
        }
        if (res.status === 404) {
          console.warn('[ProductDetails] Package types endpoint returned 404, using empty array');
          setAvailablePackageTypes([]);
          return;
        }
        throw new Error(`HTTP error! status: ${res.status}, Response: ${text.slice(0, 50)}`);
      }
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await res.text();
        console.error('[ProductDetails] Fetch package types non-JSON response:', { status: res.status, text });
        throw new Error(`Invalid response: Expected JSON, got ${contentType || 'unknown content-type'}`);
      }
      const data = await res.json();
      console.log('[ProductDetails] Fetched package types:', data);
      setAvailablePackageTypes(data || []);
    } catch (err: any) {
      console.error('[ProductDetails] Fetch package types error:', err);
      setError(`Failed to fetch package types: ${err.message}. Please try logging out and back in or contact support.`);
      setAvailablePackageTypes([]);
    }
  }, [getAuthHeaders, navigate]);

  useEffect(() => {
    if (id) {
      fetchProduct();
      fetchRecipes();
      fetchItems();
      fetchPackageTypes();
    } else {
      setProduct({
        id: 0,
        name: '',
        abbreviation: '',
        enabled: 1,
        priority: 1,
        class: ProductClass.Beer,
        type: ProductType.MaltBeverage,
        style: Style.Other,
        abv: 0,
        ibu: 0,
      });
      setPackageTypes([]);
      setAvailablePackageTypes([]);
    }
  }, [id, fetchProduct, fetchRecipes, fetchItems, fetchPackageTypes]);

  const handleSave = useCallback(async () => {
    if (!product?.name || !product.class || !product.type) {
      setError('Name, Class, and Type are required');
      return;
    }
    if (!product.abbreviation || !String(product.abbreviation).trim()) {
      setError('Abbreviation is required');
      return;
    }
    if (product.class !== ProductClass.Spirits && !product.style) {
      setError('Style is required for Beer and Wine');
      return;
    }
    const validPackageTypes = packageTypes.filter(pt => pt.type && !isNaN(parseFloat(pt.price)) && parseFloat(pt.price) >= 0);
    if (packageTypes.length !== validPackageTypes.length) {
      setError('All package types must have a valid type and non-negative price');
      return;
    }
    const headers = await getAuthHeaders();
    if (!headers) return;
    try {
      const serverClass = mapToServerClass(product.class);
      const serverType = mapToServerType(product.class, product.type);
      const serverStyle =
        product.style ||
        (product.class === ProductClass.Spirits ? Style.Other : product.style);
      const serverProduct = {
        ...product,
        abbreviation: String(product.abbreviation).trim(),
        class: serverClass,
        type: serverType,
        style: serverStyle || Style.Other,
      };
      console.log('[ProductDetails] Sending save request:', { serverProduct, packageTypes: validPackageTypes });
      const method = id ? 'PATCH' : 'POST';
      const url = id ? `${API_BASE_URL}/api/products/${id}` : `${API_BASE_URL}/api/products`;
      const res = await apiFetch(url, {
        method,
        headers,
        body: JSON.stringify({ ...serverProduct, packageTypes: validPackageTypes }),
      });
      if (!res.ok) {
        const text = await res.text();
        console.error('[ProductDetails] Save response:', { status: res.status, text });
        if (res.status === 401) {
          console.error('[ProductDetails] Unauthorized, redirecting to login');
          navigate('/login');
          throw new Error('Unauthorized: Please log in again');
        }
        throw new Error(`Failed to save product: HTTP ${res.status}, Response: ${text.slice(0, 50)}`);
      }
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await res.text();
        console.error('[ProductDetails] Save non-JSON response:', { status: res.status, text });
        throw new Error(`Invalid response: Expected JSON, got ${contentType || 'unknown content-type'}`);
      }
      const savedProduct = await res.json();
      console.log('[ProductDetails] Saved product:', savedProduct);

      // Server PATCH/POST ensures packaged items (reuses Ekos "Product (Package)" names).

      setSuccessMessage('Product saved successfully');      setTimeout(() => {
        setSuccessMessage(null);
        navigate('/products');
      }, 2000);
      setError(null);
    } catch (err: any) {
      console.error('[ProductDetails] Save error:', err);
      setError(`Failed to save product: ${err.message}`);
    }
  }, [product, packageTypes, id, getAuthHeaders, navigate]);

  const closeRecipeModal = useCallback(() => {
    setShowRecipeModal(false);
    setEditingRecipe(null);
    setError(null);
  }, []);

  const openCreateRecipe = useCallback(() => {
    setEditingRecipe(null);
    setShowRecipeModal(true);
  }, []);

  const openEditRecipe = useCallback((recipe: Recipe) => {
    setEditingRecipe(recipe);
    setShowRecipeModal(true);
  }, []);

  const handleSaveRecipe = useCallback(
    async (recipe: {
      name: string;
      productId: number | string;
      ingredients: Ingredient[];
      quantity: number;
      unit: string;
    }) => {
      if (!product || !product.id || product.id === 0) {
        setError('Cannot save recipe: Product must be saved first');
        closeRecipeModal();
        return;
      }
      const headers = await getAuthHeaders();
      if (!headers) return;
      const isEdit = !!(editingRecipe && editingRecipe.id);
      try {
        const payload = { ...recipe, productId: product.id };
        const url = isEdit
          ? `${API_BASE_URL}/api/recipes/${editingRecipe!.id}`
          : `${API_BASE_URL}/api/recipes`;
        const res = await apiFetch(url, {
          method: isEdit ? 'PATCH' : 'POST',
          headers,
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const text = await res.text();
          if (res.status === 401) {
            navigate('/login');
            throw new Error('Unauthorized: Please log in again');
          }
          throw new Error(
            `${isEdit ? 'Update' : 'Add'} recipe error: HTTP ${res.status}, Response: ${text.slice(0, 50)}`
          );
        }
        const contentType = res.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error(
            `Invalid response: Expected JSON, got ${contentType || 'unknown content-type'}`
          );
        }
        const saved = await res.json();
        if (isEdit) {
          setRecipes(recipes.map((r) => (String(r.id) === String(saved.id) ? saved : r)));
        } else {
          setRecipes([...recipes, saved]);
        }
        closeRecipeModal();
        setError(null);
      } catch (err: any) {
        console.error('[ProductDetails] Save recipe error:', err);
        setError(`Failed to save recipe: ${err.message}`);
      }
    },
    [recipes, product, editingRecipe, getAuthHeaders, navigate, closeRecipeModal]
  );

  const handleDeleteRecipe = useCallback(
    async (recipe: Recipe) => {
      if (!recipe?.id) return;
      if (!window.confirm(`Delete recipe "${recipe.name}"? This cannot be undone.`)) return;
      const headers = await getAuthHeaders();
      if (!headers) return;
      try {
        const res = await apiFetch(`${API_BASE_URL}/api/recipes/${recipe.id}`, {
          method: 'DELETE',
          headers,
        });
        if (!res.ok) {
          const text = await res.text();
          if (res.status === 401) {
            navigate('/login');
            throw new Error('Unauthorized: Please log in again');
          }
          throw new Error(`Delete recipe error: HTTP ${res.status}, Response: ${text.slice(0, 50)}`);
        }
        setRecipes(recipes.filter((r) => String(r.id) !== String(recipe.id)));
        setError(null);
      } catch (err: any) {
        console.error('[ProductDetails] Delete recipe error:', err);
        setError(`Failed to delete recipe: ${err.message}`);
      }
    },
    [recipes, getAuthHeaders, navigate]
  );

  const addPackageType = useCallback(() => {
    setPackageTypes([...packageTypes, { type: '', price: '', isKegDepositItem: false }]);
  }, [packageTypes]);

  const removePackageType = useCallback((index: number) => {
    setPackageTypes(packageTypes.filter((_, i) => i !== index));
  }, [packageTypes]);

  const updatePackageType = useCallback((index: number, field: keyof PackageType, value: string | boolean) => {
    const updatedPackageTypes = [...packageTypes];
    updatedPackageTypes[index] = { ...updatedPackageTypes[index], [field]: value };
    setPackageTypes(updatedPackageTypes);
  }, [packageTypes]);

  console.log('[ProductDetails] Render:', {
    productId: id,
    productExists: !!product,
    packageTypesLength: packageTypes.length,
    availablePackageTypesLength: availablePackageTypes.length,
    showRecipeModal,
    editingRecipeId: editingRecipe?.id ?? null,
    error,
    successMessage,
  });

  return (
    <div className="page-container container">
      <h2 className="app-header mb-4">{id ? 'Product Details' : 'Create Product'}</h2>
      {error && <div className="alert alert-danger">{error}</div>}
      {successMessage && <div className="alert alert-success">{successMessage}</div>}
      {product || !id ? (
        <div className="batch-details">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
            <div>
              <label className="form-label">Name:</label>
              <input
                type="text"
                value={product?.name || ''}
                onChange={(e) => setProduct({ ...product!, name: e.target.value })}
                placeholder="Enter product name"
                className="form-control"
              />
            </div>
            <div>
              <label className="form-label">Abbreviation:</label>
              <input
                type="text"
                value={product?.abbreviation || ''}
                onChange={(e) => setProduct({ ...product!, abbreviation: e.target.value })}
                placeholder="Enter abbreviation"
                className="form-control"
              />
            </div>
            <div>
              <label className="form-label">Enabled:</label>
              <input
                type="checkbox"
                checked={product?.enabled === 1}
                onChange={(e) => setProduct({ ...product!, enabled: e.target.checked ? 1 : 0 })}
                style={{ marginLeft: '10px' }}
              />
            </div>
            <div>
              <label className="form-label">Priority:</label>
              <input
                type="number"
                value={product?.priority || 0}
                onChange={(e) => setProduct({ ...product!, priority: parseInt(e.target.value) || 0 })}
                placeholder="Enter priority"
                className="form-control"
              />
            </div>
            <div>
              <label className="form-label">Class:</label>
              <select
                value={product?.class || ''}
                onChange={(e) => {
                  const classValue = e.target.value as ProductClass;
                  const defaultType = classValue === ProductClass.Beer ? ProductType.MaltBeverage :
                    classValue === ProductClass.Soda ? ProductType.Seltzer :
                    classValue === ProductClass.Wine ? ProductType.GrapeWine :
                    classValue === ProductClass.Spirits ? ProductType.NeutralSpirits : ProductType.MaltBeverage;
                  setProduct({
                    ...product!,
                    class: classValue,
                    type: defaultType,
                    style: undefined,
                    ibu: classValue === ProductClass.Spirits ? null : (product?.ibu ?? null),
                  });
                }}
                className="form-control"
              >
                <option value="">Select Class</option>
                {Object.values(ProductClass).map((cls) => (
                  <option key={cls} value={cls}>{cls}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Type:</label>
              <select
                value={product?.type || ''}
                onChange={(e) => {
                  const typeValue = e.target.value as ProductType;
                  setProduct({ ...product!, type: typeValue, style: undefined });
                }}
                className="form-control"
                disabled={!product?.class}
              >
                <option value="">Select Type</option>
                {product?.class &&
                  Object.values(ProductType)
                    .filter(type => {
                      if (product.class === ProductClass.Beer) {
                        return [ProductType.MaltBeverage, ProductType.Seltzer].includes(type);
                      } else if (product.class === ProductClass.Soda) {
                        return type === ProductType.Seltzer;
                      } else if (product.class === ProductClass.Wine) {
                        return [
                          ProductType.GrapeWine,
                          ProductType.SparklingWine,
                          ProductType.CarbonatedWine,
                          ProductType.FruitWine,
                          ProductType.Cider,
                          ProductType.OtherAgriculturalWine,
                        ].includes(type);
                      } else if (product.class === ProductClass.Spirits) {
                        return [
                          ProductType.NeutralSpirits,
                          ProductType.Whisky,
                          ProductType.Gin,
                          ProductType.Vodka,
                          ProductType.Rum,
                          ProductType.Tequila,
                          ProductType.CordialsLiqueurs,
                          ProductType.FlavoredSpirits,
                          ProductType.DistilledSpiritsSpecialty,
                        ].includes(type);
                      }
                      return false;
                    })
                    .map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
              </select>
            </div>
            {product?.type && product.type !== ProductType.Vodka && product.type !== ProductType.NeutralSpirits && (
              <div>
                <label className="form-label">Style:</label>
                <select
                  value={product?.style || ''}
                  onChange={(e) => setProduct({ ...product!, style: e.target.value as Style })}
                  className="form-control"
                  disabled={!product?.type}
                >
                  <option value="">Select Style</option>
                  {styleOptions[product.type]?.map(style => (
                    <option key={style} value={style}>{style}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="form-label">ABV:</label>
              <input
                type="number"
                value={product?.abv || 0}
                onChange={(e) => setProduct({ ...product!, abv: parseFloat(e.target.value) || 0 })}
                placeholder="Enter ABV"
                step="0.1"
                className="form-control"
              />
            </div>
            {product?.class !== ProductClass.Spirits && (
              <div>
                <label className="form-label">IBU:</label>
                <input
                  type="number"
                  value={product?.ibu ?? ''}
                  onChange={(e) => setProduct({ ...product!, ibu: parseInt(e.target.value) || null })}
                  placeholder="Enter IBU"
                  className="form-control"
                />
              </div>
            )}
          </div>
          <h3 className="app-header mb-3">Package Types</h3>
          {packageTypes.length === 0 && availablePackageTypes.length === 0 && (
            <div className="alert alert-warning">
              No package types available. An admin can add them under Management → Package Types.
            </div>
          )}
          {packageTypes.map((pt, index) => (
            <div key={index} className="package-type-row">
              <select
                value={pt.type}
                onChange={(e) => updatePackageType(index, 'type', e.target.value)}
                className="form-control"
              >
                <option value="">Select Package Type</option>
                {availablePackageTypes
                  .filter((pkg) => pkg.enabled)
                  .map((pkg) => (
                    <option key={pkg.name} value={pkg.name}>
                      {`${pkg.name} (${(pkg.volume * 31).toFixed(2)} gal)`}
                    </option>
                  ))}
              </select>
              <input
                type="text"
                value={pt.price}
                onChange={(e) => updatePackageType(index, 'price', e.target.value)}
                placeholder="Price (e.g., 10.00)"
                className="form-control"
              />
              <label>
                <input
                  type="checkbox"
                  checked={pt.isKegDepositItem}
                  onChange={(e) => updatePackageType(index, 'isKegDepositItem', e.target.checked)}
                />
                Is Keg Deposit Item?
              </label>
              <button
                onClick={() => removePackageType(index)}
                className="btn btn-danger"
              >
                Remove
              </button>
            </div>
          ))}
          <button
            onClick={addPackageType}
            className="btn btn-primary mt-3"
            disabled={availablePackageTypes.length === 0}
          >
            Add Package Type
          </button>
          {id && product && product.id ? (
            <div className="mt-4">
              <ProductColorField
                productId={product.id}
                productName={product.name}
                productStyle={product.style ? String(product.style) : null}
              />
            </div>
          ) : null}
          <h3 className="app-header mb-3 mt-4">Recipes</h3>
          {recipes.length > 0 ? (
            <div className="inventory-table-container">
              <table className="inventory-table table table-striped" data-testid="product-recipes-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Quantity</th>
                    <th>Ingredients</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recipes.map((recipe) => (
                    <tr key={recipe.id} data-testid={`product-recipe-row-${recipe.id}`}>
                      <td>{recipe.name}</td>
                      <td>{recipe.quantity} {recipe.unit}</td>
                      <td>
                        {recipe.ingredients && recipe.ingredients.length > 0 ? (
                          <ul style={{ margin: 0, paddingLeft: '20px' }}>
                            {recipe.ingredients.map((ing, idx) => (
                              <li key={idx}>{ing.itemName}: {ing.quantity} {ing.unit}</li>
                            ))}
                          </ul>
                        ) : (
                          'No ingredients specified'
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm btn-primary me-2"
                          data-testid={`edit-recipe-${recipe.id}`}
                          onClick={() => openEditRecipe(recipe)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          data-testid={`delete-recipe-${recipe.id}`}
                          onClick={() => handleDeleteRecipe(recipe)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="alert alert-info text-center">No recipes available.</div>
          )}
          <div className="inventory-actions mt-4">
            <button onClick={handleSave} className="btn btn-primary">
              Save Product
            </button>
            <button
              onClick={openCreateRecipe}
              className="btn btn-primary"
              data-testid="add-recipe-btn"
              disabled={!product || !product.id || product.id === 0}
            >
              Add New Recipe
            </button>
            <Link to="/products" className="btn btn-danger">
              Back to Products
            </Link>
          </div>
        </div>
      ) : (
        <div className="alert alert-info text-center">Loading...</div>
      )}
      <RecipeModal
        show={showRecipeModal}
        onClose={closeRecipeModal}
        onSave={handleSaveRecipe}
        products={product && product.id && product.id !== 0 ? [product] : []}
        items={items}
        defaultProductId={product && product.id && product.id !== 0 ? product.id : undefined}
        initialRecipe={editingRecipe}
      />
    </div>
  );
};

export default ProductDetails;